// src/pages/WorkspacePage.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { toast } from "react-toastify";
import { CheckCircle2, RotateCcw, Undo2, Video } from "lucide-react";

import { db, storage } from "../firebase";
import { useAuth } from "../context/AuthContext";
import {
  useIncidentWorkspace,
  WORKSPACE_STATUS,
} from "../hooks/useIncidentWorkspace";
import { useDensity } from "../hooks/useDensity";
import { getIncidentSeverity } from "../constants/severity";
import { isActiveStatus } from "../constants/incidentStatus";
import { generateReportId } from "../services/incidentReportService";
import {
  EVENT_CATEGORY,
  INCIDENT_EVENTS,
  recordIncidentEvent,
  summarizeIncidentEvents,
} from "../services/incidentEventService";
import {
  finalizeCompletionIfMutual,
  markClientDone,
  markHelperDone,
} from "../services/readinessService";
import { saveHistoryForBothUsers } from "../services/historyService";
import {
  addFinding,
  deleteFinding,
  setFindingStatus,
} from "../services/findingService";
import { extractResolution } from "../constants/findings";

import WorkspaceShell from "../components/workspace/shell/WorkspaceShell";
import IncidentCommandBar from "../components/workspace/shell/IncidentCommandBar";
import ContextRail from "../components/workspace/shell/ContextRail";
import CommsRail, { COMMS_TAB } from "../components/workspace/shell/CommsRail";
import IncidentTimeline from "../components/workspace/investigation/IncidentTimeline";
import RunbookPanel from "../components/workspace/investigation/RunbookPanel";
import FindingsPanel from "../components/workspace/investigation/FindingsPanel";
import ChatTab from "../components/workspace/ChatTab";
import EvidenceLibrary from "../components/workspace/investigation/EvidenceLibrary";
import NotesTab from "../components/workspace/NotesTab";
import NotesIntelligence from "../components/workspace/NotesIntelligence";
import IntelligenceCopilot from "../components/workspace/IntelligenceCopilot";
import SolvedConfirmationBanner from "../components/workspace/SolvedConfirmationBanner";
import IncidentReportForm from "../components/workspace/IncidentReportForm";
import IncidentReportView from "../components/workspace/IncidentReportView";
import IncomingCallModal from "../components/workspace/IncomingCallModal";
import PreResolutionModal from "../components/workspace/resolution/PreResolutionModal";
import WebRTCCall from "../components/call/WebRTCCall";
import SessionPanel from "../components/workspace/session/SessionPanel";
import {
  acceptCall,
  declineCall,
  endCallPresence,
  ensureCallPresenceDoc,
  heartbeatParticipantPresence,
  joinCallPresence,
  leaveCallPresence,
  markCallMissedIfRinging,
  resetCallStatus,
  setParticipantOnlineStatus,
  startRingingCall,
  subscribeToCallPresence,
  updateParticipantMediaState,
} from "../services/callPresenceService";

const EMPTY_OBJECT = {};

const MAIN_TABS = [
  { id: "findings", label: "Findings" },
  { id: "timeline", label: "Timeline" },
  { id: "runbook", label: "Runbook" },
  { id: "evidence", label: "Evidence" },
  { id: "notes", label: "Notes" },
];

/**
 * Experts open on the investigation surface because that is where they work;
 * customers open on the timeline because their question is "what is happening".
 */
const defaultTabForRole = (role) => (role === "client" ? "timeline" : "findings");

const isDesktop = () =>
  typeof window !== "undefined" && window.innerWidth >= 1024;

function WorkspacePage() {
  const { problemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, currentUserRole } = useAuth();

  const {
    problem,
    status: accessStatus,
    messages,
    files,
    updates,
    notes,
    events,
    findings,
    findingsError,
    unreadCount,
    retry: retryWorkspaceData,
  } = useIncidentWorkspace(problemId, currentUser);

  const viewerRole = useMemo(() => {
    if (!problem || !currentUser?.uid) return null;
    return problem.clientId === currentUser.uid ? "client" : "expert";
  }, [problem, currentUser?.uid]);

  const actor = useMemo(
    () => ({
      id: currentUser?.uid,
      name: currentUser?.displayName || currentUser?.email || "Participant",
      role: viewerRole || currentUserRole || null,
    }),
    [currentUser, viewerRole, currentUserRole]
  );

  // ---- Layout state -------------------------------------------------------
  const { density, toggleDensity, isCompact } = useDensity();
  const [contextOpen, setContextOpen] = useState(isDesktop);
  const [commsOpen, setCommsOpen] = useState(isDesktop);
  const [commsTab, setCommsTab] = useState(COMMS_TAB.CHAT);
  const [mainTab, setMainTab] = useState("timeline");
  // The viewer's role is not known until the problem document arrives, so the
  // opening tab is chosen once, on first resolve, rather than in useState.
  const roleDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (!viewerRole || roleDefaultAppliedRef.current) return;
    roleDefaultAppliedRef.current = true;
    setMainTab(defaultTabForRole(viewerRole));
  }, [viewerRole]);

  // ---- Composer + form state ---------------------------------------------
  const [newMessage, setNewMessage] = useState("");
  const [savingContext, setSavingContext] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showPreResolution, setShowPreResolution] = useState(false);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [taskCloseCheck, setTaskCloseCheck] = useState(null);

  // ---- Call state ---------------------------------------------------------
  const [callPresence, setCallPresence] = useState(null);
  const [sessionMounted, setSessionMounted] = useState(false);
  const [callBannerDismissed, setCallBannerDismissed] = useState(false);
  const [outgoingDismissed, setOutgoingDismissed] = useState(false);
  const [webrtcConnectionState, setWebrtcConnectionState] = useState("new");
  const [otherOfflineByHeartbeat, setOtherOfflineByHeartbeat] = useState(false);

  const workStartedRef = useRef(false);
  const preflightResultRef = useRef(null);

  // ---- Derived incident facts ---------------------------------------------
  const severity = useMemo(() => getIncidentSeverity(problem), [problem]);
  const incidentRef = useMemo(
    () => (problemId ? generateReportId(problemId) : ""),
    [problemId]
  );

  // Legacy "updates" predate the event log. Folding them into the timeline
  // keeps that history visible instead of stranding it behind a dead tab.
  const timelineEntries = useMemo(() => {
    const legacy = updates.map((update) => ({
      id: `update-${update.id}`,
      type: "legacy.update",
      category: EVENT_CATEGORY.LIFECYCLE,
      summary: update.message,
      actorName: null,
      createdAt: update.createdAt,
    }));
    return [...events, ...legacy].sort(
      (a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
    );
  }, [events, updates]);

  const timelineSummary = useMemo(
    () => summarizeIncidentEvents(events),
    [events]
  );

  const participants = callPresence?.participants || EMPTY_OBJECT;
  const otherParticipant = useMemo(
    () => Object.entries(participants).find(([uid]) => uid !== currentUser?.uid),
    [participants, currentUser?.uid]
  );
  const otherUserId = otherParticipant?.[0] || null;
  const otherPresence = otherParticipant?.[1] || EMPTY_OBJECT;

  const otherParticipantName = problem
    ? viewerRole === "client"
      ? problem.helperName || "Expert"
      : problem.clientName || problem.clientEmail || "Customer"
    : null;

  const otherOnline = Boolean(otherPresence?.online) && !otherOfflineByHeartbeat;

  const selfDisplayName = actor.name;

  const callDocStatus = callPresence?.status || null;
  const iAmInitiator =
    Boolean(callPresence?.initiatedBy) &&
    callPresence.initiatedBy === currentUser?.uid;
  const sessionLive = callDocStatus === "active";

  const hasIncomingCall =
    callDocStatus === "ringing" && !iAmInitiator && !callBannerDismissed;
  const outgoingPhase = iAmInitiator ? callDocStatus : null;
  const showOutgoingState =
    !outgoingDismissed &&
    ["ringing", "declined", "missed"].includes(outgoingPhase);

  const callInitiatorName =
    callPresence?.initiatedBy && problem
      ? callPresence.initiatedBy === problem.clientId
        ? problem.clientName || "Customer"
        : problem.helperName || "Expert"
      : "A participant";

  // ---- Access handling ----------------------------------------------------
  useEffect(() => {
    if (accessStatus === WORKSPACE_STATUS.MISSING) {
      toast.error("That incident could not be found.");
      navigate("/", { replace: true });
    } else if (accessStatus === WORKSPACE_STATUS.DENIED) {
      toast.error("You do not have access to this incident.");
      navigate("/", { replace: true });
    } else if (accessStatus === WORKSPACE_STATUS.UNASSIGNED) {
      toast.info(
        "This incident needs an expert assigned before the workspace opens."
      );
      navigate(
        viewerRole === "expert" ? "/dashboard-helper" : "/dashboard-client",
        { replace: true }
      );
    }
  }, [accessStatus, navigate, viewerRole]);

  // Deep link from the dashboard: ?openCall=1 opens the session rail.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("openCall") === "1") {
      setCommsOpen(true);
      setCommsTab(COMMS_TAB.SESSION);
    }
  }, [location.search]);

  // ---- Presence -----------------------------------------------------------
  useEffect(() => {
    if (!problemId || !currentUser?.uid || !problem) return undefined;

    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      try {
        await ensureCallPresenceDoc(
          problemId,
          [problem.clientId, problem.helperId].filter(Boolean)
        );
      } catch (error) {
        console.warn("Could not ensure call presence doc", error);
      }
      if (cancelled) return;
      unsubscribe = subscribeToCallPresence(problemId, (next) => {
        setCallPresence(next);
        if (!next?.callActive) setCallBannerDismissed(false);
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [problemId, currentUser?.uid, problem?.clientId, problem?.helperId, problem]);

  useEffect(() => {
    if (!problemId || !currentUser?.uid) return undefined;
    const beat = () =>
      heartbeatParticipantPresence(problemId, currentUser.uid).catch((error) =>
        console.warn("Presence heartbeat failed", error)
      );
    beat();
    const id = window.setInterval(beat, 30000);
    return () => window.clearInterval(id);
  }, [problemId, currentUser?.uid]);

  useEffect(() => {
    const lastSeenSec = otherPresence?.lastSeen?.seconds || null;
    if (!otherUserId || !lastSeenSec) {
      setOtherOfflineByHeartbeat(false);
      return;
    }
    setOtherOfflineByHeartbeat(Date.now() - lastSeenSec * 1000 > 90000);
  }, [otherUserId, otherPresence]);

  useEffect(() => {
    const markLeft = () => {
      if (!problemId || !currentUser?.uid) return;
      leaveCallPresence(problemId, currentUser.uid).catch(() => {});
      setParticipantOnlineStatus(problemId, currentUser.uid, false).catch(
        () => {}
      );
    };
    window.addEventListener("beforeunload", markLeft);
    return () => {
      window.removeEventListener("beforeunload", markLeft);
      markLeft();
    };
  }, [problemId, currentUser?.uid]);

  // ---- Investigation starts when the expert arrives -----------------------
  useEffect(() => {
    if (!problem || viewerRole !== "expert") return;
    if (problem.status !== "assigned" || workStartedRef.current) return;

    workStartedRef.current = true;
    (async () => {
      try {
        await updateDoc(doc(db, "problems", problemId), {
          status: "in-progress",
          inProgressAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        await recordIncidentEvent(problemId, {
          type: INCIDENT_EVENTS.WORK_STARTED,
          actor,
          summary: `${actor.name} opened the workspace and began investigating`,
        });
      } catch (error) {
        console.warn("Could not move incident to in-progress:", error);
      }
    })();
  }, [problem, viewerRole, problemId, actor]);

  // ---- Mark chat as read while the chat panel is visible ------------------
  useEffect(() => {
    if (!currentUser?.uid || !commsOpen || commsTab !== COMMS_TAB.CHAT) return;

    const unread = messages.filter(
      (m) =>
        m.senderId &&
        m.senderId !== currentUser.uid &&
        m.type !== "system" &&
        !(Array.isArray(m.readBy) && m.readBy.includes(currentUser.uid))
    );
    if (unread.length === 0) return;

    unread.forEach((message) => {
      updateDoc(doc(db, "problems", problemId, "messages", message.id), {
        readBy: arrayUnion(currentUser.uid),
      }).catch((error) => {
        if (error.code !== "permission-denied") {
          console.warn("Could not mark message as read:", error.message);
        }
      });
    });
  }, [messages, commsOpen, commsTab, currentUser?.uid, problemId]);

  // ---- Call handlers ------------------------------------------------------
  const openSessionRail = useCallback(() => {
    setCommsOpen(true);
    setCommsTab(COMMS_TAB.SESSION);
    setCallBannerDismissed(false);
  }, []);

  const handleStartCall = useCallback(
    async (preflight) => {
      openSessionRail();
      setOutgoingDismissed(false);

      // Kept so a later "the call was bad" complaint can be checked against
      // what the caller's own environment reported at the time.
      preflightResultRef.current = preflight || null;

      try {
        await startRingingCall(
          problemId,
          currentUser.uid,
          [problem?.clientId, problem?.helperId].filter(Boolean)
        );
      } catch (error) {
        console.warn("Could not start the session", error);
        toast.error("Could not start the session. Please try again.");
      }
    },
    [openSessionRail, problemId, currentUser, problem?.clientId, problem?.helperId]
  );

  const handleAcceptCall = useCallback(async () => {
    openSessionRail();
    try {
      await acceptCall(problemId, currentUser.uid);
    } catch (error) {
      console.warn("Could not accept the session", error);
      toast.error("Could not join the session.");
    }
  }, [openSessionRail, problemId, currentUser]);

  const handleDeclineCall = useCallback(async () => {
    setCallBannerDismissed(true);
    try {
      await declineCall(problemId, currentUser.uid);
    } catch (error) {
      console.warn("Could not decline the session", error);
    }
  }, [problemId, currentUser]);

  const handleIgnoreIncoming = useCallback(() => {
    setCallBannerDismissed(true);
  }, []);

  const handleCancelOutgoing = useCallback(async () => {
    setOutgoingDismissed(true);
    try {
      await resetCallStatus(problemId);
    } catch (error) {
      console.warn("Could not reset the session status", error);
    }
  }, [problemId]);

  // Mount the live session on both sides once the call is accepted.
  useEffect(() => {
    if (callDocStatus === "active") {
      setSessionMounted(true);
      openSessionRail();
    }
  }, [callDocStatus, openSessionRail]);

  // Caller-owned 30 second no-answer timeout.
  useEffect(() => {
    if (callDocStatus !== "ringing" || !iAmInitiator) return undefined;
    const timer = window.setTimeout(() => {
      markCallMissedIfRinging(problemId, { callerName: selfDisplayName }).catch(
        (error) => console.warn("Could not mark the session missed", error)
      );
    }, 30000);
    return () => window.clearTimeout(timer);
  }, [callDocStatus, iAmInitiator, problemId, selfDisplayName]);

  // NOTE: these handlers must be referentially stable. WebRTCCall lists them in
  // the dependency array of its media/signalling effect, so a new identity per
  // render tears the call down and re-runs it, which writes to the calls doc,
  // which re-renders here — an infinite loop.
  const handleCallStarted = useCallback(async () => {
    try {
      await joinCallPresence(problemId, currentUser.uid, {
        video: true,
        audio: true,
        screenSharing: false,
      });
    } catch (error) {
      console.warn("Could not mark joined presence", error);
    }
    const preflight = preflightResultRef.current;
    await recordIncidentEvent(problemId, {
      type: INCIDENT_EVENTS.SESSION_STARTED,
      actor,
      summary: `${actor.name} joined the live session`,
      metadata: preflight
        ? {
            preflight: preflight.checks.map(({ id, status }) => ({
              id,
              status,
            })),
          }
        : null,
    });
  }, [problemId, currentUser, actor]);

  const handleCallEnded = useCallback(async () => {
    try {
      await leaveCallPresence(problemId, currentUser.uid);
      await endCallPresence(problemId, currentUser.uid);
    } catch (error) {
      console.warn("Could not update leave presence", error);
    }
    setSessionMounted(false);
    await recordIncidentEvent(problemId, {
      type: INCIDENT_EVENTS.SESSION_ENDED,
      actor,
      summary: `${actor.name} ended the live session`,
    });
  }, [problemId, currentUser, actor]);

  const handleCallMediaStateChange = useCallback(
    async (patch) => {
      if (!currentUser?.uid) return;
      try {
        if ("joined" in patch && patch.joined) {
          await joinCallPresence(problemId, currentUser.uid, {
            video: patch.video ?? true,
            audio: patch.audio ?? true,
            screenSharing: patch.screenSharing ?? false,
          });
        } else if ("joined" in patch && patch.joined === false) {
          await leaveCallPresence(problemId, currentUser.uid);
        } else {
          await updateParticipantMediaState(problemId, currentUser.uid, patch);
        }
      } catch (error) {
        console.warn("Could not update participant media state", error);
      }
    },
    [problemId, currentUser]
  );

  const handleConnectionStateChange = useCallback(
    async (state) => {
      setWebrtcConnectionState(state);
      const normalized = String(state || "").toLowerCase();
      const mapped =
        normalized === "connected"
          ? "connected"
          : normalized === "connecting" || normalized === "new"
          ? "connecting"
          : ["failed", "disconnected", "closed"].includes(normalized)
          ? "disconnected"
          : "reconnecting";

      try {
        await updateParticipantMediaState(problemId, currentUser.uid, {
          online: mapped !== "disconnected",
          inCall: mapped === "connected" || mapped === "connecting",
          connectionState: mapped,
        });
      } catch (error) {
        console.warn("Could not sync connection state", error);
      }
    },
    [problemId, currentUser]
  );

  const handleCallLoadError = useCallback(() => {
    toast.error(
      "The live session could not start. Try refreshing, or use another browser."
    );
  }, []);

  // ---- Chat ---------------------------------------------------------------
  const handleSendMessage = async (e, payload = {}) => {
    e.preventDefault();
    const { attachment } = payload;
    if (!newMessage.trim() && !attachment) return;

    try {
      let attachmentUrl = null;
      let attachmentName = null;
      let attachmentType = null;
      let attachmentPreview = null;

      if (attachment) {
        const path = `problems/${problemId}/chat/${Date.now()}-${attachment.name}`;
        const uploadRef = storageRef(storage, path);
        // uploadedBy is what storage.rules checks to prove ownership.
        await uploadBytesResumable(uploadRef, attachment, {
          contentType: attachment.type || "application/octet-stream",
          customMetadata: { uploadedBy: currentUser.uid, problemId },
        });
        attachmentUrl = await getDownloadURL(uploadRef);
        attachmentName = attachment.name;
        attachmentType = attachment.type;

        if (
          attachment.type.startsWith("text/") ||
          attachment.type === "application/json" ||
          attachment.type.includes("javascript")
        ) {
          attachmentPreview = (await attachment.text()).slice(0, 5000);
        }
      }

      // The expert's first message is a milestone worth recording once.
      const isFirstExpertReply =
        viewerRole === "expert" &&
        !messages.some(
          (m) => m.senderId === problem?.helperId && m.type !== "system"
        );

      await addDoc(collection(db, "problems", problemId, "messages"), {
        senderId: currentUser.uid,
        senderName: actor.name,
        message: newMessage,
        type: "text",
        createdAt: serverTimestamp(),
        readBy: [currentUser.uid],
        attachmentUrl,
        attachmentName,
        attachmentType,
        attachmentPreview,
      });

      if (isFirstExpertReply) {
        await recordIncidentEvent(problemId, {
          type: INCIDENT_EVENTS.FIRST_RESPONSE,
          actor,
          summary: `${actor.name} sent the first response`,
        });

        // Denormalised onto the incident purely so the dashboards can flag
        // "assigned but nobody has spoken yet" without reading the event
        // subcollection once per row. The event stream stays authoritative.
        await updateDoc(doc(db, "problems", problemId), {
          firstResponseAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }).catch((error) => {
          console.error("Could not stamp first response time:", error);
        });
      }

      setNewMessage("");
      payload?.onClearAttachment?.();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message.");
    }
  };

  // ---- Evidence -----------------------------------------------------------
  const uploadEvidenceFile = useCallback(
    async (file, { source } = {}) => {
      if (!file || !problemId || !currentUser?.uid) {
        return { success: false };
      }

      const storagePath = `problems/${problemId}/files/${Date.now()}_${file.name.replace(
        /[^a-zA-Z0-9._-]/g,
        "_"
      )}`;
      const fileRef = storageRef(storage, storagePath);
      const toastId = toast.info(`Uploading ${file.name}…`, { autoClose: false });
      setUploadingEvidence(true);

      try {
        const task = uploadBytesResumable(fileRef, file, {
          contentType: file.type || "application/octet-stream",
          customMetadata: {
            uploadedBy: currentUser.uid,
            problemId,
            originalFilename: file.name,
            source: source || "upload",
          },
        });

        await new Promise((resolve, reject) => {
          task.on("state_changed", null, reject, resolve);
        });

        const downloadURL = await getDownloadURL(fileRef);

        await addDoc(collection(db, "problems", problemId, "files"), {
          uploaderId: currentUser.uid,
          uploaderName: actor.name,
          filename: file.name,
          size: file.size,
          storagePath: fileRef.fullPath,
          downloadURL,
          source: source || "upload",
          createdAt: serverTimestamp(),
        });

        await recordIncidentEvent(problemId, {
          type: INCIDENT_EVENTS.EVIDENCE_ADDED,
          actor,
          summary:
            source === "session-capture"
              ? `${actor.name} captured a session frame`
              : `${actor.name} added evidence: ${file.name}`,
          metadata: { filename: file.name, size: file.size, source: source || "upload" },
        });

        toast.dismiss(toastId);
        toast.success(
          source === "session-capture"
            ? "Frame saved to evidence."
            : "Evidence uploaded."
        );
        return { success: true };
      } catch (error) {
        toast.dismiss(toastId);
        console.error("Evidence upload failed:", error);
        toast.error(
          error.code === "storage/unauthorized"
            ? "You do not have permission to upload to this incident."
            : `Upload failed: ${error.message}`
        );
        return { success: false };
      } finally {
        setUploadingEvidence(false);
      }
    },
    [problemId, currentUser, actor]
  );

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !problem) return;
    await uploadEvidenceFile(file);
  };

  const handleCaptureFrame = useCallback(
    (file) => uploadEvidenceFile(file, { source: "session-capture" }),
    [uploadEvidenceFile]
  );

  const handleDeleteFile = async (file) => {
    if (file.isSubmissionAttachment) {
      toast.info("The customer's original attachment cannot be removed.");
      return;
    }
    try {
      if (file.storagePath) {
        await deleteObject(storageRef(storage, file.storagePath));
      }
      await deleteDoc(doc(db, "problems", problemId, "files", file.id));
      await recordIncidentEvent(problemId, {
        type: INCIDENT_EVENTS.EVIDENCE_REMOVED,
        actor,
        summary: `${actor.name} removed evidence: ${file.filename}`,
      });
      toast.success("Evidence removed.");
    } catch (error) {
      console.error("Could not delete evidence:", error);
      toast.error("Failed to remove that file.");
    }
  };

  // ---- Incident context ---------------------------------------------------
  const handleSaveContext = async (form) => {
    setSavingContext(true);
    try {
      await updateDoc(doc(db, "problems", problemId), {
        context: form,
        updatedAt: serverTimestamp(),
      });
      await recordIncidentEvent(problemId, {
        type: INCIDENT_EVENTS.CONTEXT_UPDATED,
        actor,
        summary: `${actor.name} updated the incident context`,
      });
      toast.success("Context saved.");
    } catch (error) {
      console.error("Could not save context:", error);
      toast.error("Could not save the context.");
    } finally {
      setSavingContext(false);
    }
  };

  // ---- Investigation record -----------------------------------------------
  const handleAddFinding = useCallback(
    async ({ kind, text, evidenceIds }) => {
      const result = await addFinding(problemId, {
        kind,
        text,
        author: actor,
        evidenceIds,
      });
      if (!result.success) {
        toast.error("Could not save that finding.");
      }
      return result;
    },
    [problemId, actor]
  );

  const handleSetFindingStatus = useCallback(
    async (finding, status) => {
      const result = await setFindingStatus(problemId, finding, status, actor);
      if (!result.success) toast.error("Could not update that finding.");
    },
    [problemId, actor]
  );

  const handleDeleteFinding = useCallback(
    async (findingId) => {
      const result = await deleteFinding(problemId, findingId);
      if (!result.success) toast.error("Could not delete that finding.");
    },
    [problemId]
  );

  const handleSaveNextAction = useCallback(
    async ({ nextAction, nextActionOwner }) => {
      try {
        await updateDoc(doc(db, "problems", problemId), {
          nextAction: nextAction || null,
          nextActionOwner: nextActionOwner || null,
          nextActionSetAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        console.error("Could not save the next action:", error);
        toast.error("Could not save the next action.");
      }
    },
    [problemId]
  );

  // ---- Resolution ---------------------------------------------------------
  const proceedMarkSolved = useCallback(
    async () => {
      setShowPreResolution(false);

      try {
        const problemRef = doc(db, "problems", problemId);
        const snapshot = await getDoc(problemRef);
        if (!snapshot.exists()) {
          toast.error("Incident not found.");
          return;
        }
        const problemData = { id: snapshot.id, ...snapshot.data() };
        if (problemData.status === "completed") {
          toast.info("This incident is already resolved.");
          return;
        }

        if (problemData.helperId === currentUser.uid) {
          await markHelperDone(problemId, currentUser.uid);
        } else if (problemData.clientId === currentUser.uid) {
          await markClientDone(problemId, currentUser.uid);
        }

        await recordIncidentEvent(problemId, {
          type: INCIDENT_EVENTS.RESOLUTION_CONFIRMED,
          actor,
          summary: `${actor.name} confirmed the incident is resolved`,
        });

        const result = await finalizeCompletionIfMutual(problemId);
        if (!result.success) {
          toast.error(`Could not finalise: ${result.error}`);
          return;
        }

        if (!result.completed) {
          toast.info(
            viewerRole === "expert"
              ? "Waiting for the customer to confirm before this closes."
              : "Waiting for your expert to confirm before this closes."
          );
          return;
        }

        const finalProblem = result.problem || problemData;
        await recordIncidentEvent(problemId, {
          type: INCIDENT_EVENTS.INCIDENT_RESOLVED,
          actor,
          summary: "Both parties confirmed the incident is resolved",
        });

        const historyResult = await saveHistoryForBothUsers({
          taskId: problemId,
          title: finalProblem.title,
          description: finalProblem.description,
          domain: finalProblem.domain || finalProblem.category || "General",
          clientId: finalProblem.clientId,
          helperId: finalProblem.helperId || null,
          clientName:
            finalProblem.clientName || finalProblem.clientEmail || "Customer",
          helperName: finalProblem.helperName || "Expert",
          rating: finalProblem.rating || null,
          attachment: finalProblem.attachment || null,
          createdAt: finalProblem.createdAt,
          assignedAt: finalProblem.assignedAt,
        });
        if (!historyResult.success) {
          console.error("History save failed:", historyResult.error);
        }

        await addDoc(collection(problemRef, "messages"), {
          type: "system",
          text: "This incident has been confirmed resolved by both parties.",
          createdAt: serverTimestamp(),
          senderId: currentUser.uid,
        });

        toast.success("Incident resolved.");
      } catch (error) {
        console.error("Error resolving incident:", error);
        toast.error(`Could not resolve the incident: ${error.message}`);
      }
    },
    [problemId, currentUser, actor, viewerRole]
  );

  /**
   * What the incident record will look like if it closes now.
   *
   * Blocking entries are hard requirements; the rest are quality gaps the
   * closer is warned about but may accept.
   */
  const resolutionChecks = useMemo(() => {
    const { rootCause, fix } = extractResolution(findings);
    const isExpert = viewerRole === "expert";

    const checks = [
      {
        id: "root-cause",
        label: rootCause
          ? "Root cause is documented"
          : "No root cause recorded in the investigation record",
        ok: Boolean(rootCause),
        blocking: false,
      },
      {
        id: "fix",
        label: fix
          ? "Fix applied is documented"
          : "No fix recorded in the investigation record",
        ok: Boolean(fix),
        blocking: false,
      },
    ];

    if (isExpert) {
      checks.unshift({
        id: "report",
        label: problem?.incidentReportId
          ? "Resolution report is filed"
          : "Resolution report has not been filed",
        ok: Boolean(problem?.incidentReportId),
        blocking: true,
      });
    }

    if (taskCloseCheck?.warning) {
      checks.push({
        id: "copilot",
        label: taskCloseCheck.message,
        ok: false,
        blocking: false,
      });
    }

    return checks;
  }, [findings, viewerRole, problem?.incidentReportId, taskCloseCheck]);

  const handleMarkSolved = useCallback(() => {
    // Hard gate: the expert files the resolution report before anything closes.
    if (viewerRole === "expert" && !problem?.incidentReportId) {
      setShowReportForm(true);
      return;
    }
    setShowPreResolution(true);
  }, [viewerRole, problem?.incidentReportId]);

  const handleUnresolvedCase = useCallback(async () => {
    try {
      await updateDoc(doc(db, "problems", problemId), {
        status: "unresolved",
        refundEligible: true,
        refundRequestedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await recordIncidentEvent(problemId, {
        type: INCIDENT_EVENTS.INCIDENT_UNRESOLVED,
        actor,
        summary: "Incident closed as unresolved; refund flagged for the customer",
      });
      await addDoc(collection(db, "problems", problemId, "messages"), {
        type: "system",
        text: "This incident was closed as unresolved. Our team has been notified and the customer is eligible for a full refund.",
        createdAt: serverTimestamp(),
        senderId: currentUser.uid,
      });
      await saveHistoryForBothUsers({
        taskId: problemId,
        title: problem?.title,
        description: problem?.description,
        domain: problem?.domain || problem?.category || "General",
        clientId: problem?.clientId,
        helperId: problem?.helperId || currentUser.uid,
        clientName: problem?.clientName || problem?.clientEmail || "Customer",
        helperName: problem?.helperName || actor.name,
        rating: null,
        attachment: problem?.attachment || null,
        createdAt: problem?.createdAt,
        assignedAt: problem?.assignedAt,
        status: "unresolved",
        incidentReportId: problem?.incidentReportId || null,
      });
      toast.info("Incident closed as unresolved. Our team has been notified.");
    } catch (error) {
      console.error("Error closing as unresolved:", error);
      toast.error(`Could not update the incident: ${error.message}`);
    }
  }, [problemId, problem, currentUser, actor]);

  const handleReportSubmitted = useCallback(
    async (_reportId, resolutionStatus) => {
      setShowReportForm(false);
      await recordIncidentEvent(problemId, {
        type: INCIDENT_EVENTS.REPORT_FILED,
        actor,
        summary: `${actor.name} filed the resolution report`,
        metadata: { resolutionStatus },
      });
      if (resolutionStatus === "unresolved") {
        await handleUnresolvedCase();
      } else {
        await proceedMarkSolved(true);
      }
    },
    [problemId, actor, handleUnresolvedCase, proceedMarkSolved]
  );

  const handleRequestRefund = useCallback(async () => {
    try {
      await updateDoc(doc(db, "problems", problemId), {
        refundEligible: true,
        refundRequestedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await recordIncidentEvent(problemId, {
        type: INCIDENT_EVENTS.REFUND_REQUESTED,
        actor,
        summary: `${actor.name} requested a refund`,
      });
      toast.success("Refund requested. Our team will be in touch within 24 hours.");
    } catch (error) {
      console.error("Error requesting refund:", error);
      toast.error(`Could not request the refund: ${error.message}`);
    }
  }, [problemId, actor]);

  const handleReopen = useCallback(async () => {
    try {
      await updateDoc(doc(db, "problems", problemId), {
        status: "in-progress",
        updatedAt: serverTimestamp(),
      });
      await recordIncidentEvent(problemId, {
        type: INCIDENT_EVENTS.INCIDENT_REOPENED,
        actor,
        summary: `${actor.name} reopened the incident`,
      });
      toast.success("Incident reopened.");
    } catch (error) {
      console.error("Error reopening incident:", error);
      toast.error("Could not reopen the incident.");
    }
  }, [problemId, actor]);

  // ---- Command bar actions ------------------------------------------------
  const awaitingMyConfirmation =
    (problem?.status === "waiting_client_confirmation" &&
      viewerRole === "client") ||
    (problem?.status === "waiting_expert_confirmation" &&
      viewerRole === "expert");

  const primaryAction = useMemo(() => {
    if (!problem) return null;

    if (awaitingMyConfirmation) {
      return {
        label: "Confirm resolved",
        icon: CheckCircle2,
        tone: "success",
        onClick: handleMarkSolved,
      };
    }

    if (problem.status === "completed") {
      if (viewerRole === "client") {
        return { label: "Reopen", icon: RotateCcw, onClick: handleReopen };
      }
      return null;
    }

    if (problem.status === "unresolved") {
      return problem.refundEligible
        ? null
        : { label: "Request refund", icon: Undo2, onClick: handleRequestRefund };
    }

    if (sessionLive) {
      return { label: "Open session", icon: Video, onClick: openSessionRail };
    }

    return {
      label: outgoingPhase === "ringing" ? "Calling…" : "Start live session",
      icon: Video,
      disabled: outgoingPhase === "ringing",
      onClick: handleStartCall,
    };
  }, [
    problem,
    awaitingMyConfirmation,
    viewerRole,
    sessionLive,
    outgoingPhase,
    handleMarkSolved,
    handleReopen,
    handleRequestRefund,
    handleStartCall,
    openSessionRail,
  ]);

  const secondaryActions = useMemo(() => {
    if (!problem || !isActiveStatus(problem.status) || awaitingMyConfirmation) {
      return [];
    }
    return [
      {
        label:
          viewerRole === "expert" && !problem.incidentReportId
            ? "File report & resolve"
            : "Mark resolved",
        onClick: handleMarkSolved,
      },
    ];
  }, [problem, awaitingMyConfirmation, viewerRole, handleMarkSolved]);

  // ---- Render -------------------------------------------------------------
  if (!problem) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
          <p className="text-sm text-slate-500">Opening incident workspace…</p>
        </div>
      </div>
    );
  }

  const tabs = problem.incidentReportId
    ? [...MAIN_TABS, { id: "report", label: "Resolution report" }]
    : MAIN_TABS;

  return (
    <>
      <WorkspaceShell
        density={density}
        contextOpen={contextOpen}
        commsOpen={commsOpen}
        onCloseContext={() => setContextOpen(false)}
        onCloseComms={() => setCommsOpen(false)}
        commandBar={
          <IncidentCommandBar
            incidentRef={incidentRef}
            title={problem.title}
            status={problem.status}
            severity={severity}
            viewerRole={viewerRole}
            otherParticipantName={otherParticipantName}
            otherParticipantOnline={otherOnline}
            openedAt={
              problem.createdAt?.seconds
                ? problem.createdAt.seconds * 1000
                : null
            }
            sessionMs={timelineSummary.sessionMs}
            sessionInProgress={timelineSummary.sessionInProgress}
            primaryAction={primaryAction}
            secondaryActions={secondaryActions}
            unreadCount={unreadCount}
            nextAction={problem.nextAction}
            nextActionOwner={problem.nextActionOwner}
            isCompact={isCompact}
            onToggleDensity={toggleDensity}
            contextOpen={contextOpen}
            commsOpen={commsOpen}
            onToggleContext={() => setContextOpen((v) => !v)}
            onToggleComms={() => setCommsOpen((v) => !v)}
            onBack={() =>
              navigate(
                viewerRole === "expert"
                  ? "/dashboard-helper"
                  : "/dashboard-client"
              )
            }
          />
        }
        contextRail={
          <ContextRail
            problem={problem}
            severity={severity}
            viewerRole={viewerRole}
            onSaveContext={handleSaveContext}
            saving={savingContext}
          />
        }
        main={
          <>
            {problem.status === "unresolved" && (
              <div className="flex flex-shrink-0 flex-col gap-2 border-b border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-amber-900">
                  {viewerRole === "client"
                    ? "This incident was closed as unresolved. You are eligible for a full refund."
                    : "This incident was closed as unresolved. The customer is eligible for a refund."}
                </p>
                {problem.refundEligible ? (
                  <span className="flex-shrink-0 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                    Refund requested — our team will be in touch
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleRequestRefund}
                    className="flex-shrink-0 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-rose-700"
                  >
                    Request refund
                  </button>
                )}
              </div>
            )}

            <div className="flex-shrink-0 border-b border-slate-200 px-2 sm:px-4">
              <SolvedConfirmationBanner
                problem={problem}
                currentUser={currentUser}
              />
            </div>

            <div
              role="tablist"
              aria-label="Investigation"
              className="flex flex-shrink-0 gap-1 overflow-x-auto border-b border-slate-200 px-2 py-2 sm:px-4"
            >
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={mainTab === tab.id}
                  onClick={() => setMainTab(tab.id)}
                  className={`flex-shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    mainTab === tab.id
                      ? "bg-slate-800 text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {tab.label}
                  {tab.id === "evidence" && files.length > 0 && (
                    <span className="ml-1.5 text-xs opacity-70">
                      {files.length}
                    </span>
                  )}
                  {tab.id === "findings" && findings.length > 0 && (
                    <span className="ml-1.5 text-xs opacity-70">
                      {findings.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="min-h-0 flex-1 overflow-hidden">
              {mainTab === "findings" && (
                <FindingsPanel
                  findings={findings}
                  files={files}
                  viewerRole={viewerRole}
                  nextAction={problem.nextAction}
                  nextActionOwner={problem.nextActionOwner}
                  onAddFinding={handleAddFinding}
                  onSetFindingStatus={handleSetFindingStatus}
                  onDeleteFinding={handleDeleteFinding}
                  onSaveNextAction={handleSaveNextAction}
                  error={findingsError}
                  onRetry={retryWorkspaceData}
                />
              )}
              {mainTab === "timeline" && (
                <IncidentTimeline
                  events={timelineEntries}
                  viewerRole={viewerRole}
                />
              )}
              {mainTab === "runbook" && (
                <RunbookPanel
                  problemId={problemId}
                  problem={problem}
                  currentUser={currentUser}
                  viewerRole={viewerRole}
                />
              )}
              {mainTab === "evidence" && (
                <EvidenceLibrary
                  files={files}
                  findings={findings}
                  problem={problem}
                  currentUser={currentUser}
                  onUpload={handleFileUpload}
                  onDelete={handleDeleteFile}
                  uploading={uploadingEvidence}
                />
              )}
              {mainTab === "notes" && (
                <div className="h-full space-y-4 overflow-y-auto p-4">
                  <NotesTab
                    problemId={problemId}
                    currentUser={currentUser}
                    notes={notes}
                  />
                  <NotesIntelligence
                    problemId={problemId}
                    notes={notes}
                    currentUser={currentUser}
                  />
                </div>
              )}
              {mainTab === "report" && problem.incidentReportId && (
                <div className="h-full overflow-y-auto p-4">
                  <IncidentReportView reportId={problem.incidentReportId} />
                </div>
              )}
            </div>
          </>
        }
        commsRail={
          <CommsRail
            activeTab={commsTab}
            onTabChange={setCommsTab}
            unreadCount={unreadCount}
            sessionLive={sessionLive}
            panels={{
              [COMMS_TAB.CHAT]: (
                <ChatTab
                  messages={messages}
                  newMessage={newMessage}
                  setNewMessage={setNewMessage}
                  handleSendMessage={handleSendMessage}
                  currentUser={currentUser}
                  problem={problem}
                  embedded
                  containerClassName="h-full min-h-0"
                  messagesClassName="min-h-0 flex-1 overflow-y-auto"
                />
              ),
              [COMMS_TAB.SESSION]: (
                <SessionPanel
                  sessionLive={sessionLive}
                  outgoingPhase={outgoingPhase}
                  showOutgoingState={showOutgoingState}
                  incomingFrom={hasIncomingCall ? callInitiatorName : null}
                  otherParticipantName={otherParticipantName}
                  otherPresence={otherPresence}
                  connectionState={webrtcConnectionState}
                  onStart={handleStartCall}
                  onAccept={handleAcceptCall}
                  onDecline={handleDeclineCall}
                  onCancel={handleCancelOutgoing}
                >
                  {sessionMounted && (
                    <WebRTCCall
                      problemId={problemId}
                      currentUser={currentUser}
                      currentUserRole={currentUserRole}
                      isCaller={callPresence?.callerId === currentUser?.uid}
                      remoteScreenSharing={Boolean(otherPresence?.screenSharing)}
                      remoteName={otherParticipantName}
                      onCallStarted={handleCallStarted}
                      onCallEnded={handleCallEnded}
                      onLoadError={handleCallLoadError}
                      onMediaStateChange={handleCallMediaStateChange}
                      onConnectionStateChange={handleConnectionStateChange}
                      onCaptureFrame={handleCaptureFrame}
                    />
                  )}
                </SessionPanel>
              ),
              [COMMS_TAB.ASSISTANT]: (
                <IntelligenceCopilot
                  problem={problem}
                  messages={messages}
                  files={files}
                  updates={updates}
                  notes={notes}
                  currentUser={currentUser}
                  onTaskCloseCheck={setTaskCloseCheck}
                />
              ),
            }}
          />
        }
      />

      <PreResolutionModal
        open={showPreResolution}
        viewerRole={viewerRole}
        checks={resolutionChecks}
        onClose={() => setShowPreResolution(false)}
        onProceed={proceedMarkSolved}
      />

      <IncomingCallModal
        open={hasIncomingCall}
        callerLabel={callInitiatorName}
        onAccept={handleAcceptCall}
        onDecline={handleDeclineCall}
        onIgnore={handleIgnoreIncoming}
      />

      {showReportForm && viewerRole === "expert" && (
        <IncidentReportForm
          problem={problem}
          specialistId={currentUser.uid}
          findings={findings}
          events={events}
          onClose={() => setShowReportForm(false)}
          onSubmitted={handleReportSubmitted}
        />
      )}
    </>
  );
}

export default WorkspacePage;
